/**
 * How a refused decision is shown (ALL-5908, page spec
 * `invoice-resolution-approval`).
 *
 * Every refusal a decision can produce is rendered from the service's own
 * answer: the reason code it sent, bounded, never paraphrased into something
 * else, and its support reference. Four-eyes refusals
 * (`APPROVER_MUST_DIFFER_FROM_PROPOSER`, `MACHINE_PRINCIPAL_NOT_AN_APPROVER`)
 * get their sentence from the page's copy because the contract names them; an
 * unrecognised code is shown as the bare code, so a refusal this page does not
 * understand cannot be mistaken for one it does.
 *
 * A failed step-up is called out as itself. The page does not retry it, does not
 * ask for a code, and does not complete a challenge on the operator's behalf:
 * the only step-up attempt is the one `withStepUpRetry` made, and when that
 * could not raise the session the answer is a fresh sign-in, not a second try.
 */

import { Button } from "@/components/ui/button";
import { NotAvailableState, PermissionDeniedState } from "@/components/ui/system-state";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { isDeniedCode, RESOLUTIONS_COPY as COPY } from "./copy";
import type { DecisionRefusal } from "./decisions";

export function DecisionRefusalNotice({
  refusal,
  onReload,
}: {
  refusal: DecisionRefusal;
  onReload?: () => void;
}) {
  const known = COPY.refusals[refusal.reasonCode];
  const description = known ?? "The service refused this decision.";

  // The step-up's own code is shown alongside the decision's, so support sees why
  // the session could not satisfy it as well as what the decision was refused for.
  const stepUpLine = refusal.stepUpFailed
    ? `${COPY.stepUpFailedBody}${refusal.stepUpReasonCode ? ` (Step-up refused with ${refusal.stepUpReasonCode}.)` : ""}`
    : null;

  if (isDeniedCode(refusal.reasonCode)) {
    return (
      <div data-testid="decision-refusal">
        <PermissionDeniedState
          title="This decision was refused"
          description={description}
          details={[
            { k: "Reason code", v: refusal.reasonCode },
            ...(refusal.capability ? [{ k: "Needs", v: refusal.capability }] : []),
          ]}
          action={
            onReload ? (
              <Button variant="secondary" onClick={onReload}>
                Reload proposal
              </Button>
            ) : undefined
          }
        />
        {stepUpLine && <p className="mt-3 text-xs text-muted">{stepUpLine}</p>}
        <SupportDetails reference={refusal.supportReference} className="mt-3" />
      </div>
    );
  }

  return (
    <div data-testid="decision-refusal">
      <NotAvailableState
        title={refusal.stepUpFailed ? COPY.stepUpFailedTitle : "This decision was refused"}
        description={description}
        reason={`Reason code: ${refusal.reasonCode}`}
      />
      {stepUpLine && <p className="mt-3 text-xs text-muted">{stepUpLine}</p>}
      <SupportDetails
        reference={refusal.supportReference}
        capability={refusal.capability}
        className="mt-3"
      />
      {onReload && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" onClick={onReload}>
            Reload proposal
          </Button>
        </div>
      )}
    </div>
  );
}
